import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import { Shield, Eye, EyeOff, AlertCircle, ScrollText } from 'lucide-react';

export const RegisterPage = () => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { register } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!acceptedTerms) {
            setError('You must accept the Terms and Conditions to create an account');
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (password.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }

        setLoading(true);

        try {
            await register(name, email, password);
            navigate('/start-trial');
        } catch (err) {
            let msg = 'Registration failed';
            if (err.code === 'ECONNABORTED') {
                msg = 'Request timed out. Check your connection and try again.';
            } else if (err.message === 'Network Error') {
                msg = 'Cannot reach server. Is the backend running? Check REACT_APP_BACKEND_URL.';
            } else if (err.response?.data?.detail) {
                const d = err.response.data.detail;
                msg = Array.isArray(d) ? (d[0]?.msg || d.map((x) => x.msg).filter(Boolean).join('; ') || msg) : d;
            } else if (err.message) {
                msg = err.message;
            }
            setError(typeof msg === 'string' ? msg : 'Registration failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-split" data-testid="register-page">
            {/* Terms and Conditions Modal */}
            <Dialog open={showTermsModal} onOpenChange={setShowTermsModal}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <ScrollText className="w-5 h-5" />
                            Terms and Conditions
                        </DialogTitle>
                    </DialogHeader>
                    <div className="prose prose-sm max-w-none text-muted-foreground space-y-4 py-4">
                        <p className="text-sm text-foreground font-medium">
                            Last Updated: February 2026
                        </p>
                        
                        <h3 className="text-base font-semibold text-foreground">1. Ownership</h3>
                        <p>
                            SecureSight is owned and operated by <strong>Trustpoint Security Solutions LLC</strong>. 
                            All rights, title, and interest in and to the SecureSight platform, including all 
                            intellectual property rights, are and will remain the exclusive property of 
                            Trustpoint Security Solutions LLC.
                        </p>

                        <h3 className="text-base font-semibold text-foreground">2. Disclaimer of Liability</h3>
                        <p>
                            <strong>TRUSTPOINT SECURITY SOLUTIONS LLC IS NOT RESPONSIBLE FOR ANY LOST, 
                            DAMAGED, OR CORRUPTED FILES, DATA, OR INFORMATION.</strong> The SecureSight platform 
                            is provided "as is" and "as available" without warranties of any kind, either 
                            express or implied.
                        </p>
                        <p>
                            By using this service, you acknowledge and agree that:
                        </p>
                        <ul className="list-disc pl-5 space-y-1">
                            <li>You are solely responsible for maintaining backups of your own data</li>
                            <li>Trustpoint Security Solutions LLC shall not be liable for any direct, indirect, 
                                incidental, special, consequential, or punitive damages resulting from your use 
                                of the service</li>
                            <li>The monitoring and security features are provided for informational purposes 
                                and do not guarantee complete protection against all threats</li>
                            <li>You are responsible for ensuring compliance with applicable laws and regulations 
                                in your jurisdiction</li>
                        </ul>

                        <h3 className="text-base font-semibold text-foreground">3. User Responsibilities</h3>
                        <p>
                            As a user of SecureSight, you agree to:
                        </p>
                        <ul className="list-disc pl-5 space-y-1">
                            <li>Provide accurate and complete information during registration</li>
                            <li>Maintain the security of your account credentials</li>
                            <li>Use the service only for lawful purposes</li>
                            <li>Not attempt to circumvent any security features of the platform</li>
                        </ul>

                        <h3 className="text-base font-semibold text-foreground">4. Data Collection</h3>
                        <p>
                            The SecureSight device agent collects system information including but not limited to 
                            CPU usage, memory usage, disk usage, and device identifiers. This data is used solely 
                            for providing the monitoring services you have subscribed to.
                        </p>

                        <h3 className="text-base font-semibold text-foreground">5. Subscription and Payment</h3>
                        <p>
                            By subscribing to SecureSight, you agree to pay the applicable fees for your selected 
                            plan. Subscriptions are billed on a recurring basis and can be cancelled at any time.
                        </p>

                        <h3 className="text-base font-semibold text-foreground">6. Termination</h3>
                        <p>
                            Trustpoint Security Solutions LLC reserves the right to terminate or suspend your 
                            account at any time for violation of these terms or for any other reason at our 
                            sole discretion.
                        </p>

                        <h3 className="text-base font-semibold text-foreground">7. Contact Information</h3>
                        <p>
                            For questions about these Terms and Conditions, please contact 
                            Trustpoint Security Solutions LLC.
                        </p>
                    </div>
                    <DialogFooter>
                        <Button onClick={() => setShowTermsModal(false)}>
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Left side - Hero Image */}
            <div 
                className="hidden lg:flex items-center justify-center bg-slate-900 relative overflow-hidden"
                style={{
                    backgroundImage: 'url(https://images.unsplash.com/photo-1680992046617-e2e35451bcdb?crop=entropy&cs=srgb&fm=jpg&q=85)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                }}
            >
                <div className="absolute inset-0 bg-slate-900/70" />
                <div className="relative z-10 text-white p-12 max-w-lg">
                    <Shield className="w-12 h-12 mb-6" />
                    <h1 className="font-heading text-4xl font-bold mb-4 tracking-tight">
                        Start Protecting Your Business Today
                    </h1>
                    <p className="text-lg text-slate-300 leading-relaxed">
                        Get complete visibility into your IT infrastructure with our comprehensive 
                        security and monitoring platform.
                    </p>
                </div>
            </div>

            {/* Right side - Register Form */}
            <div className="flex items-center justify-center p-8 bg-background">
                <div className="w-full max-w-md">
                    {/* Mobile Logo */}
                    <div className="flex items-center gap-2 mb-8 lg:hidden">
                        <Shield className="w-8 h-8 text-primary" />
                        <span className="font-heading text-2xl font-bold tracking-tight">SecureSight</span>
                    </div>

                    <Card className="border-border shadow-sm">
                        <CardHeader className="space-y-1 pb-4">
                            <CardTitle className="font-heading text-2xl font-bold tracking-tight">
                                Create account
                            </CardTitle>
                            <CardDescription className="text-muted-foreground">
                                Get started with SecureSight
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                {error && (
                                    <div className="flex items-center gap-2 p-3 text-sm text-destructive bg-destructive/10 rounded-md" data-testid="register-error">
                                        <AlertCircle className="w-4 h-4" />
                                        {error}
                                    </div>
                                )}

                                <div className="space-y-2">
                                    <Label htmlFor="name">Full Name</Label>
                                    <Input
                                        id="name"
                                        type="text"
                                        placeholder="John Doe"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        required
                                        data-testid="register-name-input"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="email">Email</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder="name@company.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        data-testid="register-email-input"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="password">Password</Label>
                                    <div className="relative">
                                        <Input
                                            id="password"
                                            type={showPassword ? 'text' : 'password'}
                                            placeholder="Create a password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                            className="pr-10"
                                            data-testid="register-password-input"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                        >
                                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                                    <Input
                                        id="confirmPassword"
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="Confirm your password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        required
                                        data-testid="register-confirm-password-input"
                                    />
                                </div>

                                <div className="flex items-start space-x-2 py-2">
                                    <Checkbox 
                                        id="terms" 
                                        checked={acceptedTerms}
                                        onCheckedChange={setAcceptedTerms}
                                        data-testid="accept-terms-checkbox"
                                    />
                                    <div className="grid gap-1.5 leading-none">
                                        <label
                                            htmlFor="terms"
                                            className="text-sm text-muted-foreground cursor-pointer"
                                        >
                                            I agree to the{' '}
                                            <button
                                                type="button"
                                                onClick={() => setShowTermsModal(true)}
                                                className="text-primary font-medium hover:underline"
                                                data-testid="view-terms-btn"
                                            >
                                                Terms and Conditions
                                            </button>
                                        </label>
                                    </div>
                                </div>

                                <Button 
                                    type="submit" 
                                    className="w-full" 
                                    disabled={loading}
                                    data-testid="register-submit-btn"
                                >
                                    {loading ? 'Creating account...' : 'Create account'}
                                </Button>
                            </form>

                            <div className="mt-6 text-center text-sm">
                                <span className="text-muted-foreground">Already have an account? </span>
                                <Link 
                                    to="/login" 
                                    className="text-primary font-medium hover:underline"
                                    data-testid="login-link"
                                >
                                    Sign in
                                </Link>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
};
